import 'ace-builds/src-noconflict/theme-one_dark';
import 'ace-builds/src-noconflict/theme-textmate';
import './code-editor.css';
import Component from '@glimmer/component';
import { Ace, config } from 'ace-builds';
import htmlUrl from 'ace-builds/src-noconflict/mode-html';
import jsUrl from 'ace-builds/src-noconflict/mode-javascript';
import { action } from '@ember/object';

config.setModuleUrl('ace/mode/javascript', jsUrl);
config.setModuleUrl('ace/mode/html', htmlUrl);

interface TextInput extends Ace.TextInput {
  getElement(): HTMLInputElement;
}

const DEFAULT_ACE_EDITOR_OPTIONS: Partial<Ace.EditorOptions> = {
  minLines: 10,
  maxLines: 10,
  theme: 'ace/theme/one_dark',
  mode: 'ace/mode/javascript',
  useWorker: false,
  tabSize: 2,
  useSoftTabs: true,
  navigateWithinSoftTabs: true,
  highlightActiveLine: false,
  highlightGutterLine: false,
};

interface UiInputGroupCodeEditorSignature {
  Element: HTMLDivElement;
  Args: {
    Named: {
      id: string;
      value: string;
      ariaDescribedby?: string;
      ariaKeyshortcuts?: string;
      ariaLabelledby?: string;
      onBlur?: () => void;
      onReady?: (editor: Ace.Editor) => void;
      onInput?: (value: string) => void;
      options?: Partial<Ace.EditorOptions>;
      hasError?: boolean;
      iconRight?: string;
      disableTopMargin?: boolean;
      disableLineCountDefaults?: boolean;
    };
  };
}

export default class UiInputGroupCodeEditorComponent extends Component<UiInputGroupCodeEditorSignature> {
  private _editor: Ace.Editor | undefined;
  private _boundOnBlur?: () => void;

  get options() {
    const baseOptions = this.args.disableLineCountDefaults
      ? this.optionsWithoutLineCountDefaults
      : DEFAULT_ACE_EDITOR_OPTIONS;

    return this.args.options
      ? { ...baseOptions, ...this.args.options }
      : baseOptions;
  }

  get optionsWithoutLineCountDefaults() {
    const {
      minLines: _minLines,
      maxLines: _maxLines,
      ...options
    } = DEFAULT_ACE_EDITOR_OPTIONS;

    return options;
  }

  get hasTopMargin() {
    return !this.args.disableTopMargin;
  }

  willDestroy() {
    super.willDestroy();

    this._removeTextareaEvents();
  }

  @action
  onReady(editor: Ace.Editor) {
    this.setupEditor(editor);

    this.args.onReady?.(editor);
  }

  @action
  onInput(value: string) {
    this.args.onInput?.(value);
  }

  setupEditor(editor: Ace.Editor) {
    this._editor = editor;

    this._disableTabTrap(editor);
    this._setContainerOptions(editor);
    this._setTextareaAttributes(editor);
    this._bindTextareaEvents(editor);
  }

  _disableTabTrap(editor: Ace.Editor) {
    editor.commands.removeCommand(
      editor.commands.byName['indent'] as Ace.Command
    );
    editor.commands.removeCommand(
      editor.commands.byName['outdent'] as Ace.Command
    );
  }

  _setContainerOptions(editor: Ace.Editor) {
    editor.renderer.setScrollMargin(20, 20, 0, 0);

    // @ts-expect-error setMargin is missing in types definition but present in documentation
    // Set right margin to fit icon if necessary
    // See https://ajaxorg.github.io/ace-api-docs/classes/Ace.VirtualRenderer.html#setMargin
    editor.renderer.setMargin(0, 0, 0, 40);
  }

  _setTextareaAttributes(editor: Ace.Editor) {
    if (!editor.textInput) return;

    const textareaElement: HTMLInputElement = (
      editor.textInput as TextInput
    ).getElement();

    if (this.args.id) {
      textareaElement.setAttribute('id', this.args.id);
    }

    if (this.args.ariaDescribedby) {
      textareaElement.setAttribute(
        'aria-describedby',
        this.args.ariaDescribedby
      );
    }

    if (this.args.ariaKeyshortcuts) {
      textareaElement.setAttribute(
        'aria-keyshortcuts',
        this.args.ariaKeyshortcuts
      );
    }

    if (this.args.ariaLabelledby) {
      textareaElement.setAttribute('aria-labelledby', this.args.ariaLabelledby);
    }
  }

  _bindTextareaEvents(editor: Ace.Editor) {
    if (!this.args.onBlur) return;

    this._boundOnBlur = this.args.onBlur.bind(this);
    editor.on('blur', this._boundOnBlur);
  }

  _removeTextareaEvents() {
    if (!this._boundOnBlur || !this._editor) return;

    this._editor.session.off('blur', this._boundOnBlur);
    delete this._boundOnBlur;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::InputGroup::CodeEditor': typeof UiInputGroupCodeEditorComponent;
    'ui/input-group/code-editor': typeof UiInputGroupCodeEditorComponent;
  }
}
